// ============================================================================
// FNE-BRIDGE - Store Clients / Fournisseurs (Zustand)
// ============================================================================

import { create } from 'zustand';
import type { Client } from '../types/models';

// ============================================================================
// Types du state
// ============================================================================

interface ClientState {
  // --- Liste ---
  clients: Client[];
  clientsLoading: boolean;
  clientsError: string | null;
  clientsTotal: number;
  clientsPage: number;
  clientsLimit: number;

  // --- Client courant ---
  currentClient: Client | null;
  currentClientLoading: boolean;

  // --- Filtres ---
  filters: {
    search: string;
    type_contact: string;
    est_actif: boolean | undefined;
  };

  // --- Actions ---
  setClients: (clients: Client[], total: number) => void;
  setClientsLoading: (loading: boolean) => void;
  setClientsError: (error: string | null) => void;
  setClientsPage: (page: number) => void;
  setClientsLimit: (limit: number) => void;
  setCurrentClient: (client: Client | null) => void;
  setCurrentClientLoading: (loading: boolean) => void;
  addClient: (client: Client) => void;
  updateClient: (id: string, updates: Partial<Client>) => void;
  removeClient: (id: string) => void;
  setFilters: (filters: Partial<ClientState['filters']>) => void;
  reset: () => void;
}

// ============================================================================
// Store
// ============================================================================

const initialFilters = {
  search: '',
  type_contact: '',
  est_actif: undefined as boolean | undefined,
};

export const useClientStore = create<ClientState>()((set) => ({
  clients: [],
  clientsLoading: false,
  clientsError: null,
  clientsTotal: 0,
  clientsPage: 1,
  clientsLimit: 20,
  currentClient: null,
  currentClientLoading: false,
  filters: { ...initialFilters },

  setClients: (clients, total) => set({ clients, clientsTotal: total, clientsLoading: false }),
  setClientsLoading: (clientsLoading) => set({ clientsLoading }),
  setClientsError: (clientsError) => set({ clientsError, clientsLoading: false }),
  setClientsPage: (clientsPage) => set({ clientsPage }),
  setClientsLimit: (clientsLimit) => set({ clientsLimit, clientsPage: 1 }),

  setCurrentClient: (currentClient) => set({ currentClient, currentClientLoading: false }),
  setCurrentClientLoading: (currentClientLoading) => set({ currentClientLoading }),

  addClient: (client) =>
    set((state) => ({
      clients: [client, ...state.clients],
      clientsTotal: state.clientsTotal + 1,
    })),

  updateClient: (id, updates) =>
    set((state) => ({
      clients: state.clients.map((c) => (c.id === id ? { ...c, ...updates } : c)),
      currentClient:
        state.currentClient?.id === id
          ? { ...state.currentClient, ...updates }
          : state.currentClient,
    })),

  removeClient: (id) =>
    set((state) => ({
      clients: state.clients.filter((c) => c.id !== id),
      clientsTotal: state.clientsTotal - 1,
      currentClient: state.currentClient?.id === id ? null : state.currentClient,
    })),

  setFilters: (newFilters) =>
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
      clientsPage: 1,
    })),

  reset: () =>
    set({
      clients: [],
      clientsLoading: false,
      clientsError: null,
      clientsTotal: 0,
      clientsPage: 1,
      currentClient: null,
      currentClientLoading: false,
      filters: { ...initialFilters },
    }),
}));
